import { Link, useMatches, useMatchRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  ACTIVITIES__NEW__,
  NAV_ITEMS__NEW__,
  type NavItem__NEW__,
} from "@/lib/constants";
import { cn } from "@/lib/utils";
import { Image } from "../image";

export const useActiveActivity = () => {
  const matches = useMatches();
  const matchRoute = useMatchRoute();

  const activeActivity = useMemo(() => {
    for (const activity of Object.values(ACTIVITIES__NEW__)) {
      if (!("to" in activity.link)) continue;
      const match = matchRoute(activity.link);
      if (match !== false) {
        return activity;
      }
    }
    return null;
  }, [matchRoute, matches]);

  return activeActivity;
};

export function DesktopNav() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    // Set initial state on first render
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div
      className={cn(
        "fixed inset-x-0 top-0 z-20 hidden text-foreground transition-colors duration-300 md:block",
        isScrolled ? "bg-background" : "bg-transparent"
      )}
      key="desktop-nav"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-[auto_auto_auto] items-center p-4 lg:grid-cols-3">
        <div className="flex items-center gap-4">
          <Link to="/">
            <Image
              alt="Logo"
              className="h-6 object-contain"
              height={32}
              src="/images/logo.png"
              width={210}
            />
          </Link>
        </div>
        <NavigationMenu className="justify-self-center" delayDuration={0}>
          <NavigationMenuList>
            <NavigationMenuItem>
              <NavigationMenuTrigger className="bg-transparent">
                Angebote
              </NavigationMenuTrigger>
              <NavigationMenuContent className="w-fit border-none bg-background p-4">
                <ul className="grid gap-3 md:w-[400px] lg:w-[500px] lg:grid-cols-[1fr_1fr]">
                  {Object.values(ACTIVITIES__NEW__).map((activity) => (
                    <NavItem
                      backgroundImage={activity.brand.backgroundImage}
                      description={activity.shortDescription}
                      item={{ ...activity, label: activity.name }}
                      key={activity.key}
                    />
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            {NAV_ITEMS__NEW__.map((item) => {
              if (!item.items) {
                return (
                  <NavigationMenuItem key={item.key}>
                    <NavigationMenuLink asChild>
                      <Link {...item.link}>{item.label}</Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                );
              }

              return (
                <NavigationMenuItem key={item.key}>
                  <NavigationMenuTrigger className="bg-transparent">
                    {item.label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="border-none bg-background p-4">
                    <ul className="grid gap-3 md:w-[400px] lg:w-[500px] lg:grid-cols-[1fr_1fr]">
                      {item.items.map((childItem) => (
                        <NavItem item={childItem} key={childItem.key} />
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              );
            })}
          </NavigationMenuList>
        </NavigationMenu>
        <BookingButton />
      </div>
    </div>
  );
}

function BookingButton() {
  const activity = useActiveActivity();
  const { label, ...bookingLink } =
    activity && "bookingLink" in activity
      ? activity.bookingLink
      : {
          label: "Jetzt buchen",
          to: "/buchen",
        };
  const backgroundColor = activity?.brand.primaryColor || "var(--primary)";
  const foregroundColor =
    activity?.brand.primaryForegroundColor || "var(--primary-foreground)";

  return (
    <Button
      asChild
      className="w-fit justify-self-end bg-[var(--booking-button-background-color)] text-[var(--booking-button-foreground-color)] hover:bg-[var(--booking-button-background-color)]/90"
      size="lg"
      style={
        {
          "--booking-button-background-color": backgroundColor,
          "--booking-button-foreground-color": foregroundColor,
        } as React.CSSProperties
      }
      variant="default"
    >
      <Link {...bookingLink}>{label}</Link>
    </Button>
  );
}

function NavItem({
  item,
  backgroundImage,
  description,
}: {
  item: NavItem__NEW__;
  backgroundImage?: string;
  description?: string;
}) {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          {...item.link}
          className={cn(
            "block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors",
            backgroundImage
              ? "data-[active=true]:hover:!bg-white relative overflow-hidden text-white hover:text-white"
              : "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
          )}
        >
          {backgroundImage && (
            <>
              <Image
                alt={item.label}
                className="absolute inset-0 z-0 w-full object-cover object-bottom"
                height={65}
                src={backgroundImage}
                width={244}
              />
              <div className="absolute inset-0 z-0 bg-black/50" />
            </>
          )}
          <div className="z-10 mb-1 font-medium text-sm leading-none">
            {item.label}
          </div>
          <p className="z-10 line-clamp-2 text-sm leading-snug">
            {description}
          </p>
        </Link>
      </NavigationMenuLink>
    </li>
  );
}
